"use client";

import { format } from "date-fns";
import { ChevronDown, ChevronUp, PanelRightClose } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import type { EconomicEvent } from "@/vybe-trader-client/economic-calendar/types";
import { Button } from "@/shadcn-components/ui/button";
import { cn } from "@/lib/utils";
import { EconomicSeriesCharts } from "./EconomicSeriesCharts";
import type { ImpactLevel } from "./impact-styles";
import {
  impactBadgeClassName,
  impactLucideIcons,
} from "./impact-styles";
import { currencyPairLabel } from "./currency-flag-emoji";
import { parseEconomicValue } from "./parse-economic-value";
import { useEconomicSeriesDetail } from "./useEconomicSeriesDetail";

const PARSEABLE_HINT = 2;

export function countParseableSeriesFields(
  occurrences: readonly EconomicEvent[],
  field: "forecast" | "actual"
): number {
  let n = 0;
  for (const e of occurrences) {
    const raw = field === "forecast" ? e.forecast : e.actual;
    if (parseEconomicValue(raw) !== null) n += 1;
  }
  return n;
}

/** Charts + history (no chrome) — reused by settings full-screen detail */
export function EconomicSeriesDetailBody({
  occurrences,
  className,
}: {
  occurrences: readonly EconomicEvent[];
  className?: string;
}) {
  const fcCount = countParseableSeriesFields(occurrences, "forecast");
  const acCount = countParseableSeriesFields(occurrences, "actual");
  const sparse = fcCount < PARSEABLE_HINT && acCount < PARSEABLE_HINT;
  const newestFirst = [...occurrences].reverse();

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {sparse ? (
        <p className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
          Sparse numeric history: forecasts or actuals are often non-numeric for
          this event; charts omit non-parseable releases.
        </p>
      ) : null}

      <EconomicSeriesCharts occurrences={occurrences} />

      <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border">
        <div className="shrink-0 border-b border-border bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Release history ({occurrences.length})
        </div>
        <HistoryTable rows={newestFirst} />
      </div>
    </div>
  );
}

export function EconomicSeriesDetailPanel({
  seriesKey,
  allEvents,
  subtitle,
  className,
  onHidePanel,
  headerExtra,
}: {
  seriesKey: string | null;
  allEvents: readonly EconomicEvent[];
  /** e.g. “From selected day · Jan 14” */
  subtitle?: string;
  className?: string;
  onHidePanel?: () => void;
  headerExtra?: ReactNode;
}) {
  const { occurrences, canonicalEvent } = useEconomicSeriesDetail(
    seriesKey,
    allEvents
  );
  const [bodyCollapsed, setBodyCollapsed] = useState(false);

  if (!seriesKey || !canonicalEvent) {
    return (
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col items-center justify-center border-l border-border bg-muted/10 px-4 text-center",
          className
        )}
      >
        <p className="text-[12px] font-medium text-foreground">
          Select an event
        </p>
        <p className="mt-1 max-w-[220px] text-[11px] text-muted-foreground">
          Choose a row in the day schedule or catalog to see forecast and
          actual history for that series.
        </p>
      </div>
    );
  }

  const imp = canonicalEvent.impact as ImpactLevel;
  const Icon = impactLucideIcons[imp];
  const pair = currencyPairLabel(canonicalEvent.currency);

  return (
    <div
      className={cn(
        "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-l border-border bg-background",
        className
      )}
    >
      <div className="flex shrink-0 flex-col gap-1 border-b border-border bg-muted/15 px-2 py-1.5">
        <div className="flex items-start gap-1">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h2 className="truncate text-[13px] font-semibold leading-tight text-foreground">
                {canonicalEvent.name}
              </h2>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded border border-border px-1 py-px text-[10px] font-mono tabular-nums">
                <span aria-hidden>{pair.flag}</span>
                {pair.codeUpper}
              </span>
              <span
                className={cn(
                  "inline-flex max-w-full items-center gap-0.5 truncate rounded border px-1 py-px text-[9px]",
                  impactBadgeClassName(imp)
                )}
              >
                <Icon className="h-2.5 w-2.5 shrink-0" />
                <span className="truncate">{imp}</span>
              </span>
            </div>
            {(subtitle ?? headerExtra) ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {subtitle ? (
                  <p className="text-[10px] text-muted-foreground">{subtitle}</p>
                ) : null}
                {headerExtra}
              </div>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              aria-expanded={!bodyCollapsed}
              aria-label={bodyCollapsed ? "Expand charts & history" : "Collapse charts & history"}
              onClick={() => setBodyCollapsed((v) => !v)}
              title={bodyCollapsed ? "Show charts & table" : "Hide charts & table"}
            >
              {bodyCollapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </Button>
            {onHidePanel ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                aria-label="Hide detail pane"
                onClick={onHidePanel}
                title="Hide detail pane"
              >
                <PanelRightClose className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {!bodyCollapsed ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-2">
          <EconomicSeriesDetailBody occurrences={occurrences} />
        </div>
      ) : null}
    </div>
  );
}

function HistoryTable({ rows }: { rows: EconomicEvent[] }) {
  if (rows.length === 0) {
    return (
      <div className="px-2 py-4 text-center text-[11px] text-muted-foreground">
        No rows.
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div
        className="grid shrink-0 border-b border-border bg-muted/20 text-[9px] font-medium uppercase tracking-wide text-muted-foreground"
        style={{
          gridTemplateColumns: "92px minmax(0, 1fr) 44px 44px 44px",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <HCell>Date</HCell>
        <HCell>Event</HCell>
        <HCell>Prev</HCell>
        <HCell>Fcst</HCell>
        <HCell>Act</HCell>
      </div>
      {rows.map((e, idx) => {
        const dt = safeFormat(e.dateTimeISO, "MMM d, yyyy");
        const tm =
          e.timeLabel?.trim() ||
          safeFormat(e.dateTimeISO, "HH:mm");
        return (
          <div
            key={`${e.dateTimeISO}-${e.name}-${idx}`}
            className="grid border-b border-border text-[10px] leading-snug hover:bg-muted/25"
            style={{
              gridTemplateColumns: "92px minmax(0, 1fr) 44px 44px 44px",
            }}
          >
            <div className="px-1 py-1 font-mono tabular-nums text-muted-foreground">
              <div>{dt}</div>
              <div className="text-[9px]">{tm}</div>
            </div>
            <div className="min-w-0 truncate px-1 py-1" title={e.name}>
              {e.name}
            </div>
            <Cell mono>{dash(e.previous)}</Cell>
            <Cell mono>{dash(e.forecast)}</Cell>
            <Cell mono>{dash(e.actual)}</Cell>
          </div>
        );
      })}
    </div>
  );
}

function HCell({ children }: { children: ReactNode }) {
  return <div className="truncate px-1 py-1">{children}</div>;
}

function Cell({
  children,
  mono,
}: {
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <div
      className={cn(
        "truncate px-1 py-1 text-foreground",
        mono && "font-mono tabular-nums"
      )}
    >
      {children}
    </div>
  );
}

function dash(s: string | undefined) {
  const t = s?.trim();
  return t ? t : "–";
}

function safeFormat(iso: string, pat: string) {
  try {
    return format(new Date(iso), pat);
  } catch {
    return "—";
  }
}
